import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Row,
  Skeleton,
  Tabs,
  Upload,
  Spin,
  Progress,
} from "antd";
import {
  CheckOutlined,
  UploadOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useHistory, useParams, Prompt } from "react-router-dom";

import {
  deleteAdviseePolicy,
  postCreateAdviseeOwnedPolicy,
  patchAdviseePolicy,
  useGetAdviseePolicy,
} from "apis/advisee";

import { uploadPolicy } from "apis/policy";

import { buildPPSOwnedPoliciesUri } from "utils/routes";

import ClickableTextButton from "components/ClickableTextButton/ClickableTextButton";
import ScrollableContainer from "components/ScrollableContainer/ScrollableContainer";
import Title from "components/Title/Title";

import CoverageTab from "./components/Coverage/Coverage";
import FundDistribution from "./components/FundDistribution/FundDistribution";
import Nomination from "./components/Nomination/Nomination";
import OtherInfo from "./components/OtherInfo/OtherInfo";
import PlanDetails from "./components/PlanDetails/PlanDetails";

import { DeleteCol, StyledTabPane } from "./OwnedPolicyForm.styles";
import { jsx } from "@emotion/core";
import Locale from "react-intl-universal";

const { useForm } = Form;
const { confirm, success, error } = Modal;

const FORMAT_TYPE_OBJ_TO_ARR = "object-to-array";
const FORMAT_TYPE_ARR_TO_OBJ = "array-to-object";

const formatBenefits = (benefitValues, type) => {
  if (type === FORMAT_TYPE_OBJ_TO_ARR) {
    const benefitsInArr = [];
    Object.keys(benefitValues).forEach((key) => {
      const currentBenefit = benefitValues[key];
      if (currentBenefit && currentBenefit.amount) {
        benefitsInArr.push({
          type: key,
          amount: currentBenefit.amount,
          label: currentBenefit.label || undefined,
          remarks: currentBenefit.remarks || undefined,
        });
      }
    });
    return benefitsInArr;
  } else if (type === FORMAT_TYPE_ARR_TO_OBJ) {
    const benefitsInObj = {};
    benefitValues.forEach((benefitValue) => {
      benefitsInObj[benefitValue.type] = {
        amount: benefitValue.amount || undefined,
        remarks: benefitValue.remarks || undefined,
        label: benefitValue.label || undefined,
      };
    });
    return benefitsInObj;
  }
  return benefitValues;
};

const formatAdviseeCoverages = (coverages, type) => {
  if (type === FORMAT_TYPE_OBJ_TO_ARR) {
    return Object.keys(coverages)
      .filter((key) => coverages[key])
      .map((key) => ({
        type: key,
      }));
  } else if (type === FORMAT_TYPE_ARR_TO_OBJ) {
    const coveragesInObj = {
      deathWaiver: false,
      tpdWaiver: false,
      ciWaiver: false,
    };
    coverages.forEach((coverage) => {
      coveragesInObj[coverage.type] = true;
    });
    return coveragesInObj;
  }
  return coverages;
};

const formatPolicyDetails = (ownedPolicy) => {
  return {
    ...ownedPolicy,
    commencementDate: ownedPolicy.commencementDate
      ? moment(ownedPolicy.commencementDate, "YYYY-MM-DD")
      : undefined,
    maturityDate: ownedPolicy.maturityDate
      ? moment(ownedPolicy.maturityDate, "YYYY-MM-DD")
      : undefined,
    yearlyGuaranteedCashPayment: ownedPolicy.yearlyGuaranteedCashPayment
      ? {
          ...ownedPolicy.yearlyGuaranteedCashPayment,
          startingDate: ownedPolicy.yearlyGuaranteedCashPayment.startingDate
            ? moment(
                ownedPolicy.yearlyGuaranteedCashPayment.startingDate,
                "YYYY-MM-DD"
              )
            : undefined,
          maturityDate: ownedPolicy.yearlyGuaranteedCashPayment.maturityDate
            ? moment(
                ownedPolicy.yearlyGuaranteedCashPayment.maturityDate,
                "YYYY-MM-DD"
              )
            : undefined,
        }
      : undefined,
    hospitalizationBenefits:
      ownedPolicy.benefits && ownedPolicy.benefits.hospitalizationBenefits
        ? formatBenefits(
            ownedPolicy.benefits.hospitalizationBenefits,
            FORMAT_TYPE_ARR_TO_OBJ
          )
        : undefined,
    femaleBenefits:
      ownedPolicy.benefits && ownedPolicy.benefits.femaleBenefits
        ? formatBenefits(
            ownedPolicy.benefits.femaleBenefits,
            FORMAT_TYPE_ARR_TO_OBJ
          )
        : undefined,
    investmentFunds: ownedPolicy.investmentFunds
      ? formatBenefits(ownedPolicy.investmentFunds, FORMAT_TYPE_ARR_TO_OBJ)
      : undefined,
    isCoverForOther: { value: !!ownedPolicy.coveredFor },
    medicalCard: ownedPolicy.medicalCard
      ? {
          ...ownedPolicy.medicalCard,
          lifetimeLimitAmt:
            ownedPolicy.medicalCard.lifetimeLimitAmt > -1
              ? ownedPolicy.medicalCard.lifetimeLimitAmt
              : undefined,
          isLifetimeLimitAmtUnlimited:
            ownedPolicy.medicalCard.lifetimeLimitAmt === -1 || false,
        }
      : undefined,
    adviseeCoverages:
      ownedPolicy.adviseeCoverages && ownedPolicy.adviseeCoverages.length > 0
        ? formatAdviseeCoverages(
            ownedPolicy.adviseeCoverages,
            FORMAT_TYPE_ARR_TO_OBJ
          )
        : undefined,
  };
};

const checkIfBenefitsObjectHasValue = (obj) => {
  if (obj) {
    const valuesOfObj = Object.values(obj);
    if (valuesOfObj.length > 0) {
      const valuesInArr =
        valuesOfObj.filter((value) => value.amount || value.amount >= 0) || [];
      return valuesInArr.length > 0;
    }
    return false;
  }
  return false;
};

const formatPayloadBody = (
  defaultValues,
  values,
  planCoverages,
  nominations,
  investmentFunds
) => {
  const isHospitalizationHasValue = checkIfBenefitsObjectHasValue(
    values.hospitalizationBenefits
  );
  const isFemaleBenefitsHasValue = checkIfBenefitsObjectHasValue(
    values.femaleBenefits
  );
  return {
    name: values.name,
    benefits:
      planCoverages.length > 0 ||
      isHospitalizationHasValue ||
      isFemaleBenefitsHasValue
        ? {
            planCoverages:
              planCoverages.length > 0
                ? planCoverages
                : defaultValues.benefits &&
                  defaultValues.benefits.planCoverages,
            hospitalizationBenefits: isHospitalizationHasValue
              ? formatBenefits(
                  values.hospitalizationBenefits,
                  FORMAT_TYPE_OBJ_TO_ARR
                )
              : defaultValues.benefits &&
                defaultValues.benefits.hospitalizationBenefits,
            femaleBenefits: isFemaleBenefitsHasValue
              ? formatBenefits(values.femaleBenefits, FORMAT_TYPE_OBJ_TO_ARR)
              : defaultValues.benefits && defaultValues.benefits.femaleBenefits,
          }
        : undefined,
    investmentFunds: investmentFunds.length > 0 ? investmentFunds : undefined,
    nominations: nominations.length > 0 ? nominations : undefined,
    commencementDate: values.commencementDate
      ? moment(values.commencementDate).format("YYYY-MM-DD")
      : undefined,
    maturityDate: values.maturityDate
      ? moment(values.maturityDate).format("YYYY-MM-DD")
      : undefined,
    yearlyGuaranteedCashPayment: values.yearlyGuaranteedCashPayment
      ? {
          ...values.yearlyGuaranteedCashPayment,
          startingDate: values.yearlyGuaranteedCashPayment.startingDate
            ? moment(values.yearlyGuaranteedCashPayment.startingDate).format(
                "YYYY-MM-DD"
              )
            : undefined,
          maturityDate: values.yearlyGuaranteedCashPayment.startingDate
            ? moment(values.yearlyGuaranteedCashPayment.startingDate).format(
                "YYYY-MM-DD"
              )
            : undefined,
        }
      : undefined,
    medicalCard: values.medicalCard
      ? {
          ...values.medicalCard,
          lifetimeLimitAmt: values.medicalCard.isLifetimeLimitAmtUnlimited
            ? -1
            : values.medicalCard.lifetimeLimitAmt,
          isLifetimeLimitAmtUnlimited: undefined,
        }
      : undefined,
    adviseeCoverages:
      values.isCoverForOther &&
      values.isCoverForOther.value &&
      values.adviseeCoverages
        ? formatAdviseeCoverages(
            values.adviseeCoverages,
            FORMAT_TYPE_OBJ_TO_ARR
          )
        : undefined,
  };
};

const OwnedPolicyForm = ({ adviseeInfo }) => {
  const { id: adviseeId, policyId } = useParams();
  const history = useHistory();
  const [form] = useForm();
  const {
    isLoading: isOwnedPolicyLoading,
    data: ownedPolicy,
  } = useGetAdviseePolicy(adviseeId, policyId);
  const [selectedSuggestedPolicy, setSelectedSuggestedPolicy] = useState({});
  const [selectedType, setSelectedType] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [planCoverages, setPlanCoverages] = useState([]);
  const [investmentFunds, setInvestmentFunds] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [defaultValues, setDefaultValues] = useState({});
  const isDataLoading = policyId && isOwnedPolicyLoading;
  const isNew = !policyId && !Object.keys(ownedPolicy).length > 0;

  const [loading, setLoading] = useState(false);

  const [uploadVisible, setUploadVisible] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);

  const [saveReminder, setSaveReminder] = useState(false);
  const [benefitShareCheck, setBenefitShareCheck] = useState(false);

  const [
    uploadPolicyQuestionVisible,
    setUploadPolicyQuestionVisible,
  ] = useState(false);

  useEffect(() => {
    const policy = isNew ? selectedSuggestedPolicy : ownedPolicy;

    if (Object.keys(policy).length) {
      if (policy.benefits) {
        setPlanCoverages(policy.benefits.planCoverages);
      }
      if (policy.nominations) {
        setNominations(policy.nominations);
      }
      if (policy.investmentFunds) {
        setInvestmentFunds(policy.investmentFunds);
      }
      if (policy.type) {
        setSelectedType(policy.type);
      }
      if (policy.company) {
        setSelectedCompany(policy.company.type);
      }
      const formattedPolicy = formatPolicyDetails(policy);
      setDefaultValues(formattedPolicy);

      form.setFieldsValue(formattedPolicy);
      console.log("formattedPolicy", formattedPolicy);
    }
  }, [ownedPolicy, selectedSuggestedPolicy, isNew, form, history]);

  const handleOnSaveButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const updatedPlanCoverages = planCoverages
        .filter((obj) => obj.amount > 0)
        .map((obj) => ({
          type: obj.type,
          remarks: obj.remarks,
          term: obj.term ? obj.term : 0,
          amount: obj.amount,
          label: obj.label
            ? obj.label
            : obj.type === "otherBenefit"
            ? "Others"
            : undefined,
        }));

      const payload = {
        ...values,
        ...formatPayloadBody(
          defaultValues,
          values,
          updatedPlanCoverages,
          nominations,
          investmentFunds
        ),
      };
      console.log(payload, 295);
      setSaveReminder(false);

      payload.nominations = form.getFieldValue("nominations");

      if (!isNew) {
        // Nomination Details (Benefit Share) checking max is 100 //

        const FormNomination = form.getFieldValue("nominations");
        var FormBenefitShare = 0;

        if (FormNomination && FormNomination.length > 0) {
          FormNomination.forEach((val) => {
            // console.log('val.benefitShare', val.benefitShare);
            FormBenefitShare += val.benefitShare;
          });

          if (FormBenefitShare > 100) {
            setBenefitShareCheck(true);
            message.error("(Nomination Details) Benefit Share (%) max is 100");
            return;
          } else if (FormBenefitShare !== 100) {
            setBenefitShareCheck(true);
            message.error(
              "(Nomination Details) Benefit Share (%) total must be 100"
            );
            return;
          }
        }

        // Nomination Details (Benefit Share) checking max is 100 ^ //

        patchAdviseePolicy(adviseeId, policyId, payload)
          .then((res) => {
            setBenefitShareCheck(false);
            message.success(
              Locale.get("ownedPolicies.newPolicyForm.updateSuccess")
            );
          })
          .catch((ex) => {
            message.error(
              "Error in updating policy details. Please check your connection and try again."
            );
          });
      } else {
        // Nomination Details (Benefit Share) checking max is 100 //

        const FormNomination = form.getFieldValue("nominations");
        var FormBenefitShare = 0;

        console.log("FormNomination", FormNomination);

        if (FormNomination && FormNomination.length > 0) {
          FormNomination.forEach((val) => {
            // console.log('val.benefitShare', val.benefitShare);
            FormBenefitShare += val.benefitShare;
          });

          if (FormBenefitShare > 100) {
            setBenefitShareCheck(true);
            message.error("(Nomination Details) Benefit Share (%) max is 100");
            return;
          } else if (FormBenefitShare !== 100) {
            setBenefitShareCheck(true);
            message.error(
              "(Nomination Details) Benefit Share (%) total must be 100"
            );
            return;
          }
        }

        // Nomination Details (Benefit Share) checking max is 100 ^ //

        postCreateAdviseeOwnedPolicy(adviseeId, payload)
          .then((res) => {
            success({
              title: "Policy has been added!",
              okText: "Back to View All",
              onOk() {
                setBenefitShareCheck(false);
                history.push(buildPPSOwnedPoliciesUri(adviseeId));
              },
            });
          })
          .catch((ex) => {
            error({
              title: "Unable to create this policy.",
            });
          });
      }
    } catch (error) {
      // console.log('nomination', form.getFieldValue('nominations'));

      console.log("error", error);

      error.errorFields.forEach((field) => {
        if (field.name.length === 3) {
          switch (field.name[0]) {
            case "nominations":
              return message.error(
                `(Nomination Details) Row-${field.name[1] + 1} ${
                  field.errors[0]
                }`
              );

            case "trustee":
              return message.error(
                `(Trustee Details) Row-${field.name[1] + 1} ${field.errors[0]}`
              );

            case "assignee":
              return message.error(
                `(Assignee Details) Row-${field.name[1] + 1} ${field.errors[0]}`
              );

            case "contingent":
              return message.error(
                `(Contingent Details) Row-${field.name[1] + 1} ${
                  field.errors[0]
                }`
              );

            default:
              break;
          }
        } else {
          return message.error(field.errors[0]);
        }
      });
    }
  };

  const handleOnDeleteButtonClick = () => {
    confirm({
      title: Locale.get("ownedPolicies.newPolicyForm.removePolicy"),
      okText: Locale.get("ownedPolicies.newPolicyForm.yes"),
      onOk() {
        deleteAdviseePolicy(adviseeId, policyId)
          .then((res) => {
            message.success(
              Locale.get("ownedPolicies.newPolicyForm.deletePolicy")
            );
            history.push(buildPPSOwnedPoliciesUri(adviseeId));
          })
          .catch((ex) => {
            message.error(
              "Error in deleting policy. Please check your connection and try again."
            );
          });
      },
      onCancel() {},
    });
  };

  const UploadPolicy = (file) => {
    console.log("file", file);

    const adviseeName = adviseeInfo.name;
    const adviseeId = adviseeInfo._id;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("adviseeName", adviseeName);
    formData.append("adviseeId", adviseeId);

    setLoading(true);
    setUploadVisible(true);
    message.warning("AI is processing your policy. Please wait.");

    uploadPolicy(formData, (progress) => {
      console.log("progress", progress);

      setUploadPercentage(Math.round((progress.loaded / progress.total) * 100));
    })
      .then((res) => {
        console.log("res", res);

        // if (res.status === 200) {
        setLoading(false);
        setUploadPercentage(0);

        message.success(
          "Policy uploaded successfully! Please kindly check the policy details."
        );

        res.commencementDate = moment(res.commencementDate, "YYYY-MM-DD");
        res.maturityDate = moment(res.maturityDate, "YYYY-MM-DD");

        if (res?.yearlyGuaranteedCashPayment?.startingDate) {
          res.yearlyGuaranteedCashPayment.startingDate = moment(
            res.yearlyGuaranteedCashPayment.startingDate,
            "YYYY-MM-DD"
          );
        }

        if (res?.yearlyGuaranteedCashPayment?.maturityDate) {
          res.yearlyGuaranteedCashPayment.maturityDate = moment(
            res.yearlyGuaranteedCashPayment.maturityDate,
            "YYYY-MM-DD"
          );
        }

        res.coveredFor
          ? (res.isCoverForOther = { value: true })
          : (res.isCoverForOther = { value: false });

        if (res.benefits) {
          setPlanCoverages(res.benefits.planCoverages);
        }
        if (res.nominations) {
          setNominations(res.nominations);
        }
        if (res.investmentFunds) {
          setInvestmentFunds(res.investmentFunds);
        }
        if (res.type) {
          setSelectedType(res.type);
        }
        if (res.company) {
          setSelectedCompany(res.company.type);
        }

        const formattedPolicy = formatPolicyDetails(res);

        setDefaultValues({ ...res, ...formattedPolicy });
        form.setFieldsValue({ ...res, ...formattedPolicy });

        setTimeout(() => {
          setUploadVisible(false);
        }, 100);
        // }
      })
      .catch((ex) => {
        setLoading(false);
        Modal.error({
          title: ex.message,
        });
      });
  };

  const UploadPolicyQuestionModal = (
    <Modal
      visible={uploadPolicyQuestionVisible}
      title="AI Policy Reader"
      // afterClose={() => form.resetFields()}
      onCancel={() => {
        setUploadPolicyQuestionVisible(false);
      }}
      okButtonProps={{ style: { display: "none" } }}
      cancelText="Close"
      // onOk={handleSubmitContact}
      // okButtonProps={{ loading: loading }}
      // cancelButtonProps={{ disabled: loading }}
    >
      <div>
        <Row style={{ marginBottom: 16, fontWeight: 500 }}>
          The Finport AI policy reader will automatically extract and populate
          the policy form based on the uploaded policy.
        </Row>
        <div>
          <ul style={{ padding: "0px 12px" }}>
            <li style={{ listStyleType: "decimal", marginBottom: 8 }}>
              Upload policy file in *non-scanning PDF format only.
            </li>

            <li style={{ listStyleType: "decimal", marginBottom: 8 }}>
              Wait for the AI to process the policy (approximately 60 seconds).
            </li>

            <li style={{ listStyleType: "decimal", marginBottom: 8 }}>
              The policy details will be auto-filled in the respective fields.
            </li>

            <li style={{ listStyleType: "decimal", marginBottom: 8 }}>
              Kindly review the populated policy details.
            </li>
          </ul>
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      <Spin
        spinning={loading}
        tip={
          <div>
            {uploadVisible
              ? uploadPercentage < 100
                ? "Uploading Policy..."
                : "Processing Policy, it might take a while, please do not close the browser during the processing..."
              : // <Progress type="circle" percent={uploadPercentage} />
                "Loading..."}
          </div>
        }
      >
        <Row justify="end" align="middle" style={{ marginBottom: "8px" }}>
          {isNew && (
            <Col>
              <Row align="middle">
                <Col>
                  <QuestionCircleOutlined
                    style={{
                      marginRight: 16,
                      fontSize: 20,
                      cursor: "pointer",
                      color: "rgba(0,0,0,0.45)",
                    }}
                    onClick={() => {
                      setUploadPolicyQuestionVisible(true);
                    }}
                  />
                </Col>

                <Col style={{ marginRight: 8 }}>
                  <Upload
                    name="logo"
                    // accept="image/*"
                    accept="application/pdf"
                    // action="/upload.do"
                    // listType="picture-card"
                    disabled={loading}
                    showUploadList={{
                      showDownloadIcon: false,
                      showPreviewIcon: false,
                      showRemoveIcon: true,
                    }}
                    // onPreview={handlePreview}
                    // fileList={uploadFile}
                    fileList={[]}
                    onChange={({ file, fileList }) => {
                      const regex = new RegExp(/.pdf$/);

                      if (!regex.test(file.name)) {
                        message.error("File type must in PDF format");
                      } else {
                        UploadPolicy(file);
                      }
                    }}
                    beforeUpload={() => false}
                    // withCredentials
                  >
                    <Button type="primary" icon={<UploadOutlined />}>
                      Upload Policy
                    </Button>
                  </Upload>
                </Col>
              </Row>
            </Col>
          )}

          <Row align="middle">
            {!isNew && (
              <DeleteCol>
                <ClickableTextButton
                  text={Locale.get("ownedPolicies.newPolicyForm.delete")}
                  onClick={handleOnDeleteButtonClick}
                />
              </DeleteCol>
            )}
            <Col span={12}>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleOnSaveButtonClick}
              >
                {Locale.get("ownedPolicies.newPolicyForm.save")}
              </Button>
            </Col>
          </Row>
        </Row>
        <ScrollableContainer>
          <Form
            form={form}
            initialValues={defaultValues}
            onChange={(e) => setSaveReminder(true)}
          >
            {!isNew && isDataLoading ? (
              <Skeleton active />
            ) : (
              <>
                <Title marginBottom="16px">
                  {isNew
                    ? Locale.get("ownedPolicies.newPolicyForm.newPolicy")
                    : ownedPolicy.name}
                </Title>
                <Tabs defaultActiveKey="planDetails">
                  <StyledTabPane
                    tab={Locale.get("ownedPolicies.newPolicyForm.planDetails")}
                    key="planDetails"
                    forceRender
                  >
                    <PlanDetails
                      isNew={isNew}
                      selectedType={selectedType}
                      selectedCompany={selectedCompany}
                      updateSelectedSuggestedPolicy={setSelectedSuggestedPolicy}
                      updateSelectedType={setSelectedType}
                      updateSelectedCompany={setSelectedCompany}
                    />
                  </StyledTabPane>
                  <StyledTabPane
                    tab={Locale.get("ownedPolicies.coverageTab.coverage")}
                    key="Coverage"
                    forceRender
                  >
                    <CoverageTab
                      defaultValues={defaultValues}
                      updatePlanCoverage={setPlanCoverages}
                      updateSelectedType={setSelectedType}
                      planCoverages={planCoverages}
                      uploadVisible={uploadVisible}
                    />
                  </StyledTabPane>
                  <StyledTabPane
                    tab={Locale.get(
                      "ownedPolicies.fundDistributionTab.fundDistribution"
                    )}
                    key="fundDistribution"
                    forceRender
                  >
                    <FundDistribution
                      investmentFunds={
                        investmentFunds.length > 0
                          ? investmentFunds
                          : ownedPolicy.investmentFunds || []
                      }
                      updateInvestmentFunds={(newInvestmentFunds) =>
                        setInvestmentFunds(newInvestmentFunds)
                      }
                    />
                  </StyledTabPane>
                  <StyledTabPane
                    tab={Locale.get("ownedPolicies.nominationTab.nomination")}
                    key="nomination"
                    forceRender
                  >
                    <Nomination
                      form={form}
                      nominations={
                        nominations.length > 0
                          ? nominations
                          : ownedPolicy.nominations || []
                      }
                      updateNominations={(newNominations) =>
                        setNominations(newNominations)
                      }
                      benefitShareCheck={benefitShareCheck}
                    />
                  </StyledTabPane>
                  <StyledTabPane
                    tab={Locale.get("ownedPolicies.otherInfoTab.otherInfo")}
                    key="otherInfo"
                    forceRender
                  >
                    <OtherInfo />
                  </StyledTabPane>
                </Tabs>
              </>
            )}
          </Form>

          {/* <DeleteCol>
                <ClickableTextButton
                  text="Delete"
                  onClick={handleOnDeleteButtonClick}
                />
              </DeleteCol> */}
        </ScrollableContainer>
      </Spin>
    </>
  );
};

export default OwnedPolicyForm;
